/**
 * Belgeden stok girişi istemcisi — `POST /api/stock-intake/parse` ve `POST /api/stock/shipments/receive`.
 *
 * `parse` YALNIZ TASLAK döner, deftere hiçbir şey yazmaz. Kaydetme TEK irsaliye olarak
 * `receive` ucuna gider: çok kalemli irsaliye sunucuda TEK transaction'da yazılır (bir kalem
 * geçersizse hiçbiri yazılmaz). Satır satır `POST /api/stock/shipments` çağrısı yapılmaz —
 * irsaliye numarası kalemler arasında ortak olduğu için satır satır gönderim 409'a düşerdi.
 */
import { useMutation } from '@tanstack/react-query';
import { api } from './client';

export interface StockIntakeCandidate {
  productId: string;
  name: string;
  score: number;
}

export interface StockIntakeLine {
  rawName: string;
  productId: string | null;
  brandId: string | null;
  candidates: StockIntakeCandidate[];
  qtyFull: number;
  qtyEmptyOut: number;
  date: string | null;
  waybillNo: string | null;
  confidence: 'high' | 'low';
}

export interface StockIntakeResult {
  lines: StockIntakeLine[];
  warnings: string[];
  source: 'text' | 'ai';
}

export type StockIntakeParseInput =
  { text: string } | { file: { base64: string; mime: string; filename?: string } };

/** Metin (Excel yapıştırma) ya da fotoğraf/PDF taslağı okur. */
export function useStockIntakeParse() {
  return useMutation({
    mutationFn: (input: StockIntakeParseInput) =>
      api.post<StockIntakeResult>('/api/stock-intake/parse', input),
  });
}

/** Çok kalemli irsaliyenin tek kalemi: ürün + gelen dolu adet + isteğe bağlı boş çıkış. */
export interface StockIntakeReceiveLine {
  productId: string;
  qty: number;
  /**
   * Kalemin BOŞ ÇIKIŞI (fabrikadan alınan boş). Panel hesap YAPMAZ: kullanıcının girdiği
   * adet olduğu gibi gider (kural 4 — hesap backend'de).
   */
  qtyEmptyOut: number;
}

/**
 * Tek irsaliye gövdesi. `waybillNo` ZORUNLU (sunucu reddeder), `brandId` kalemlerin ortak
 * markasıdır — panel HESAP YAPMAZ, yalnızca seçilen ürünlerin markasını okur.
 */
export interface StockIntakeReceiveInput {
  brandId: string;
  waybillNo: string;
  lines: StockIntakeReceiveLine[];
  note?: string;
  /** İstek anahtarı — sunucu aynı `clientId`yi ikinci kez görürse yeni satır yazmaz. */
  clientId: string;
}

/** Çok kalemli irsaliyeyi tek çağrıda kaydeder (sunucu: TEK transaction, ürün başına bir satır). */
export function useStockIntakeReceive() {
  return useMutation({
    mutationFn: (input: StockIntakeReceiveInput) =>
      api.post<{ shipments: { id: string }[] }>('/api/stock/shipments/receive', input),
  });
}

/** Dosyayı base64'e çevirir (multipart YOK — görev tanımı kısıtı: yeni bağımlılık eklenmez). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // "data:image/jpeg;base64,AAAA..." → yalnız veri kısmı.
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('dosya okunamadı'));
    reader.readAsDataURL(file);
  });
}
